import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Loading } from "@carbon/react";
import { ArrowLeft } from "@carbon/icons-react";
import PageHeader from "../../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { EditSiteForm } from "../../../components";
import { useID } from "../../../hooks";
import { useSite } from "../../../hooks/useSites";
import "./editSite.scss";

/**
 * The edit-site screen's container — rebuilt on the Carbon primitives (Task 6 addendum) in place
 * of a Tailwind `<h2>` heading and `GoBackArrow`. `EditSiteForm.tsx` itself (the same task)
 * renders no heading of its own, so `PageHeader` here is the page's only `<h1>` — same situation
 * `EditUser.tsx` is in (its own form renders no heading either), NOT `UserGroups.tsx`'s situation
 * (that one deliberately skips `PageHeader` because `Users.tsx`, its parent, already rendered
 * one for the shared `/users` route both tabs sit under). `/sites/:id` has no such parent
 * heading — `PageHeader` belongs here.
 *
 * `GoBackArrow` (`components/GoBackArrow/GoBackArrow.tsx`) is a shared Tailwind component still
 * used by ~10 other, unmigrated screens (Opportunity, Delivery, the Bid family, EditMaterial,
 * ...) — it is NOT touched here. This screen's back affordance is a plain Carbon ghost `Button`
 * instead, the same swap `containers/User/user.scss`'s own `.user__back` comment already
 * describes making for `User.tsx`: "same role GoBackArrow played on the Tailwind version of this
 * page, rebuilt as a plain Carbon ghost button instead of a bespoke icon." `navigate(-1)` matches
 * `GoBackArrow`'s own default behaviour with no props (this screen never passed `isOffer` or hit
 * its `/material/offer` special case), so the navigation target is unchanged — only the button's
 * markup is. Unlike the bare icon-only `GoBackArrow` (no visible text, and its icon is
 * `aria-hidden` — so today it has NO accessible name at all), this button carries real visible
 * text (`sites.back`), which doubles as its accessible name.
 *
 * `Loading`, not the shared `Loader`: `Users.tsx`/`User.tsx` are the named precedent for this
 * screen's loading state, and neither uses `Loader` — `User.tsx` (the one Users-flow screen with
 * graceful fetch-error handling, see below) uses Carbon's own `Loading`, and `Users.tsx` uses
 * `DataTable`'s built-in skeleton. (`EditUser.tsx`, the OTHER Users-flow edit screen, still uses
 * the shared `Loader` — but it also has no error handling to model, which is exactly the gap
 * this component fixes. Matching `User.tsx`'s complete loading+error block keeps both states
 * consistent with each other, rather than pairing `Loader` from one screen with error handling
 * borrowed from a different one.)
 *
 * `useSite(id)` error handling: previously `GET /site/{id}` failing (an unresolved id, a
 * cross-tenant fetch — `SiteController#getSite` 400s that, see its own comment on the exploit
 * this closed) left `isLoading` false, `site` `undefined`, and this screen still rendered
 * `<EditSiteForm site={undefined} />` — which, after this task's rewrite, shows `EditSiteForm`'s
 * own `!site` guard, a permanent `Loading` spinner with no way out. This screen now mirrors
 * `User.tsx`'s three-way branch (`isLoading` / `isError || !site` / real content) instead,
 * showing `sites.notFound` — the same pattern, not a bespoke one.
 */
const EditSite = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useID();
  const { data: site, isLoading, isError } = useSite(id);

  return (
    <div>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={ArrowLeft}
        onClick={() => navigate(-1)}
        className="edit-site__back"
      >
        {t("sites.back")}
      </Button>

      {isLoading ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : isError || !site ? (
        <EmptyState title={t("sites.notFound")} />
      ) : (
        <>
          <PageHeader title={t("sites.edit")} />
          <EditSiteForm site={site} />
        </>
      )}
    </div>
  );
};

export default EditSite;
